import { LonaIcons, TextIcon } from "@tento-ui/ui/icons";
import type { NaiveDate } from "@tento-chrono";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import {
  type BindCellContext,
  RenderPlugin,
  RowOptionsElement,
  type SheetLegendClass,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { Actor, type Option, RowId } from "@tento-lona/sheets";
import { ColumnTextRow } from "@tento-lona/sheets/platform";
import { TextCell } from "./column-text/support/text-cell.ts";
import "./column-text/support/text-cell.ts";
import {
  HomeCardFactory,
  SheetRowTextCard,
  TextPromptSubtitle,
} from "@tento-lona/sheets-ui/home-card/index.ts";
import { CellTarget, type SheetRow } from "@tento-lona";
import { COLUMN_TEXT_SPEC } from "@tento-lona/sheets/platform";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";
import type { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { Tracing } from "@tento-core/tracing";
import { storageCellDateForColumn } from "./cell-date.ts";
import { CellCommitBaseline } from "./cell-commit-baseline.ts";

function textCellDateForColumn(
  timescaleSetting: SheetRow.Joined.Handle["timescaleSetting"],
  column: ColumnIndex.Key.Resolved.Default,
): NaiveDate.Partial {
  return storageCellDateForColumn(timescaleSetting, column);
}

/** The renderer plugin for a text row: one editable text per column. */
export class ColumnTextRendererPlugin
  implements
    RenderPlugin.Renderer<TextCell, SheetLegend.DefaultRow, ColumnTextRow>
{
  static readonly spec: PerTypeSpec = COLUMN_TEXT_SPEC;

  readonly role = "renderer";
  readonly id = "column.text";
  readonly rowClass: typeof ColumnTextRow = ColumnTextRow;
  readonly icon: HTMLTemplateElement = LonaIcons.$template(TextIcon);

  /** Per-(rowId, column) actor for self-echo suppression — see
   *  `RenderPlugin.CellActors`. Keyed by logical `(row, week)`, not by cell
   *  instance, so it stays on the plugin. Per-cell lifecycle state (the data
   *  subscription and the `(row, week)` identity) lives on the `TextCell`. */
  #cellActors = new RenderPlugin.CellActors("column.text");

  constructor(
    public readonly makeRowOptions: RowOptionsElement.Factory = RowOptionsElement.Factory
      .NOOP,
  ) {}

  onCellRecycled($cell: TextCell): void {
    $cell.setCellHandle(null);
    $cell.destroyEditor();
  }

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return "Your defacto row to take notes. Use it to summarize class lectures or meeting minutes.";
  }

  makeCell(): TextCell {
    const $c = TextCell.make();
    $c.assignStyles({
      height: "var(--height)",
      width: "calc(100% - 4px)",
      marginLeft: "2px",
      "--editor-width": "300px",
    });
    return $c;
  }

  async bindCell(
    $cell: TextCell,
    {
      sheet,
      row,
      sheetRow,
      column,
      isCurrent,
      inThePast,
      handle,
      widthPx,
    }: BindCellContext<ColumnTextRow>,
  ) {
    const cellActor = this.#cellActors.get(sheetRow.id, column);

    if (row.sheetReads.registries.render.diagnostics()) {
      Tracing.log("text/bindCell", {
        rowId: sheetRow.id.toString(),
        rowLabel: sheetRow.label,
        column: column.toString(),
        isCurrent,
      });
    }

    const [visualDate] = column.discrete();
    const cellDate = textCellDateForColumn(sheetRow.timescaleSetting, column);
    const route = cellPersistenceRouteSummary(visualDate, cellDate);
    const prompt = row.prompt;
    const placeholder = isCurrent ? prompt : undefined;

    // Track the latest committed value, not just the value captured by the
    // current bind. Self-echo writes intentionally do not rebind the editor, so
    // a user can type text and then clear it in the same live editor. Each
    // write's refusal takes back only that write (by identity): the baseline
    // falls back to the newest write still standing or the confirmed text,
    // never to a refused predecessor. Another writer's change observed while
    // a write is pending is confirmed, but the pending write's later
    // acceptance still confirms it over that change (arrival order).
    const committed = new CellCommitBaseline<string>();
    const commit = (md: string) => {
      const newData = md === "" ? null : md;
      Tracing.log("cell-persist/1-commit", {
        rowId: row.id.toString(),
        localId: sheetRow.id.toString(),
        rowLabel: sheetRow.label,
        column: column.toString(),
        date: cellDate.toString(),
        actor: cellActor.name,
        route,
        isClear: newData === null,
        len: newData?.length ?? 0,
        committedBefore: committed.value,
        suppressed: newData === committed.value,
      });
      if (newData === committed.value) return;
      // The row's own checked cell write (a text row has one cell per
      // column: its single-cardinality facade). A refusal changed nothing
      // stored: the write stops counting toward the baseline, so committing
      // the same text again writes again instead of being taken for a no-op,
      // and the refusal is reported — never left floating.
      const cells = sheetRow.writableSingle();
      if (cells == null) {
        Tracing.warn("ui")("column-text: the row is not single-cardinality", { rowId: sheetRow.id.toString() });
        return;
      }
      const target = CellTarget.date(cellDate);
      const write = committed.begin(newData);
      const written = newData == null ? cells.clear(target, cellActor) : cells.write(target, newData, cellActor);
      written.then(
        () => committed.accepted(write),
        (err: unknown) => {
          committed.refused(write);
          Tracing.warn("net")("column-text/write failed", { rowId: sheetRow.id.toString(), error: String(err) });
        },
      );
    };

    const bindWithData = (data: Option<string>, loading: boolean = false) => {
      if (!loading) committed.bind(data);
      if (row.sheetReads.registries.render.diagnostics()) {
        Tracing.log("text/bindWithData", {
          rowId: row.id.toString(),
          column: column.toString(),
          cellDate: cellDate.toString(),
          hasData: data != null,
          dataLen: data?.length ?? 0,
          dataPreview: data?.slice(0, 48) ?? null,
          loading,
        });
      }
      $cell.bind({
        onUpdate: commit,
        markdown: data,
        editable: sheet.editable && row.editable,
        placeholder,
        handle,
        column,
        rowId: RowId.fromLocalId(sheetRow.id),
        localId: sheetRow.id,
        widthPx,
        inThePast,
        loading,
      });
    };

    // Adopt the new data subscription (disposes the prior one) and record this
    // cell's `(row, week)` identity on the cell itself.
    const single = sheetRow.single();
    if (single == null) return;
    const h = single.cell(cellDate);
    $cell.setCellHandle(h);
    // Key the editor identity off the row's process-STABLE id, not its wire
    // string. An ephemeral→server promote re-keys the row IN PLACE (same
    // `RowLocalId` object, mutated `.toString()`); using the wire string here
    // would make the post-promote re-key look like a new (row, week), take the
    // `identityChanged` branch below, and recreate the focused ProseMirror
    // editor — the COW flash + lost caret. `serial` survives the re-key, so a
    // promote leaves the editor untouched and the post-promote rebinds fall
    // through to the guarded `onData` path (which preserves focus).
    const identityChanged = $cell.adoptIdentity(
      `${sheetRow.id.serial}|${cellDate.toString()}`,
    );

    // On reuse for a new (row, week), rebind the editor to the cached value
    // synchronously — `CellHandle.onData` below is deferred to a microtask, so
    // this closes the window where a stray edit (or the expand-popup's
    // `currentOnUpdate`) would persist stale content to the wrong week. Bind
    // with `loading: false` (the cached value, or empty when uncached): this
    // clears any previous week's text and re-points the persist target without
    // forcing the loading shimmer on every empty cell — the shimmer is owned by
    // `onLoading` for genuinely pending fetches and cleared by `onData`.
    // Same-identity rebinds (resize / re-render) keep the live editor state,
    // including any focused edit, via the guarded `onData` path below.
    if (identityChanged) {
      // Witness for the COW flash + lost-caret bug: the identity-changed branch
      // recreates the editor and is the ONE rebind path that bypasses the
      // `:focus-within` guards in `onData`/`onLoading` below. Legitimately it
      // fires only when a pooled cell is reused for a different (row, week) —
      // which never happens to a FOCUSED cell. So an identity change while the
      // editor is focused means the caret is about to be dropped; a COW promote
      // re-keys IN PLACE (stable `serial`) and MUST NOT trip this. A ux-test
      // forbids this label, so a regression that re-keys the cell identity off
      // the mutable wire string fails loudly instead of silently eating focus.
      if ($cell.matches(":focus-within")) {
        Tracing.log("cell-identity-clobber-focused", {
          serial: sheetRow.id.serial,
          rowId: sheetRow.id.toString(),
          cellDate: cellDate.toString(),
        });
      }
      const cached = single.peek(cellDate)?.value;
      bindWithData(typeof cached === "string" ? cached : null, false);
    }

    h.onLoading(() => {
      if ($cell.matches(":focus-within")) return;
      bindWithData(null, true);
    });
    h.onData((cell, change) => {
      const raw = cell?.value;
      const data = raw !== null && typeof raw === "string" ? raw : null;
      // Self-echo: this fire is from this cell's own write — keep live
      // editor state (caret, selection) intact, but update the committed
      // baseline so the next clear/edit in the same editor is not suppressed.
      if (change?.actor === cellActor) {
        committed.echo(data);
        $cell.toggleAttribute("loading", false);
        return;
      }
      const editorValue = $cell.value;
      // While the cell is still showing the loading shimmer, resolved data MUST
      // bind (which drops the `loading` attribute and binds the editor) even if
      // it equals the editor's stale value — otherwise empty cells (data and
      // editorValue both null) stay stuck in the shimmer forever. The guards
      // below are optimizations/safety for the already-loaded path only.
      const loading = $cell.hasAttribute("loading");
      if (loading && editorValue != null && $cell.matches(":focus-within")) {
        $cell.toggleAttribute("loading", false);
        if (data === editorValue) committed.bind(data);
        return;
      }
      if (!loading) {
        // Defense-in-depth (the earlier Layer 2 focus guard): never clobber a
        // focused live edit. Removable once the actor pattern is proven across
        // all editable plugins.
        if (editorValue != null && $cell.matches(":focus-within")) return;
        // Fast-path: round-trip equals live value.
        if (data === editorValue) return;
      }
      bindWithData(data);
    });

    $cell.onfocus = () => {};
  }

  onResize($cell: TextCell, _context: BindCellContext<ColumnTextRow>): void {
    $cell.refreshExpandState();
  }

  loadData = null;
  legendFactory: Option<SheetLegendClass<SheetLegend.DefaultRow>> = null;

  onBindRow(_sheet: SheetDelegate, _row: SheetRow.Joined.Handle): void {}
  onViewportColumnsChanged(): void {}
  onRenderedColumnsWillChange(): void {}

  homeCardFactory: HomeCardFactory<SheetRowTextCard> = {
    make: (ctx) => ({
      $cardContent: SheetRowTextCard.makeWith({
        value: "",
        onUpdate: (markdown) => writeTextDurable(ctx.row, ctx.datePartial, markdown),
      }),
      $listContent: TextPromptSubtitle.makeWith({
        date: ctx.datePartial,
        placeholder: "Add an entry",
        onSubmit: (markdown) => writeTextDurable(ctx.row, ctx.datePartial, markdown),
      }),
      $listAccessory: null,
    }),

    bind: (content, ctx) => {
      const raw = ctx.row.single()?.peek(ctx.datePartial)?.value ?? null;
      content.$cardContent.bind({
        value: typeof raw === "string" ? raw : "",
        onUpdate: (markdown) => writeTextDurable(ctx.row, ctx.datePartial, markdown),
      });
    },
  };
}

/**
 * Write a text row's cell for `day` durably, through the row's
 * single-cardinality facade (a text row has one cell per column); the
 * refusal is reported, never left floating.
 */
function writeTextDurable(row: SheetRow.Joined.Handle, day: NaiveDate.Partial, markdown: string): void {
  const cells = row.writableSingle();
  if (cells == null) {
    Tracing.warn("ui")("column-text: the row is not single-cardinality", { rowId: row.id.toString() });
    return;
  }
  cells.writeDurable(CellTarget.date(day), markdown, Actor.EXTERNAL).settled.catch((err: unknown) =>
    Tracing.warn("net")("column-text/card write failed", { rowId: row.id.toString(), error: String(err) })
  );
}

function cellPersistenceRouteSummary(
  visualDate: NaiveDate.Partial,
  storageDate: NaiveDate.Partial,
): {
  readonly visual: ReturnType<typeof cellPersistenceDateSummary>;
  readonly storage: ReturnType<typeof cellPersistenceDateSummary>;
  readonly sameRange: boolean;
} {
  return {
    visual: cellPersistenceDateSummary(visualDate),
    storage: cellPersistenceDateSummary(storageDate),
    sameRange: cellPersistenceSameRange(visualDate, storageDate),
  };
}

function cellPersistenceDateSummary(date: NaiveDate.Partial): {
  readonly key: string;
  readonly start: string;
  readonly end: string;
  readonly startDse: number;
  readonly endDse: number;
} {
  return {
    key: date.toString(),
    start: date.start.toString(),
    end: date.end.toString(),
    startDse: date.start.dse,
    endDse: date.end.dse,
  };
}

function cellPersistenceSameRange(
  a: NaiveDate.Partial,
  b: NaiveDate.Partial,
): boolean {
  return a.start.dse === b.start.dse && a.end.dse === b.end.dse;
}

/** Types and values that go with `ColumnTextRendererPlugin`: `Attributes`, `cellDateForColumn`. */
export namespace ColumnTextRendererPlugin {
  export interface Attributes {
    prompt?: string;
  }

  export const cellDateForColumn = textCellDateForColumn;
}
